import React from 'react';
import type { ComponentProps } from 'react';
import { useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';
import Header from '../../components/Header/Header';
import {
    canDisplayCallOptOutControls,
    canDisplayOnlineButton,
    canDisplaySnoozingControls,
    canShowDashboardLink,
    getElapsedSeconds,
    getSuggestionCallFrequency,
    getWhitelistPhrasesSpottingScore,
    hasTrainerError,
    isCallSimulatorEnabled,
    isOptInOn,
    isOptOut,
    isTrainerActive,
    isTrainerCallStarted,
    isTrainerEnabled,
    isTrainerStatusUpdating,
    isSalesEnabled,
    isTranscriptVisible,
    shouldRecordCall
} from '../../store/selectors/trainer';
import { resumeTrainer, snoozeTrainer, setInfinitySnooze, manualOptIn, manualOptOut } from '../../store/actions/trainer';
import { closeCallSuggestions, toggleCallSuggestions } from '../../store/actions/callSuggestions';
import { doLogout } from '../../store/actions/user';
import { IS_UI_ONLY_MODE } from '../../config/electron';
import useLocalization from '../../localization/useLocalization';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getAuthRole, getAuthToken, isAuthWithPasswordEnabled, isVodafoneUser } from '../../store/selectors/auth';
import { getUserScores, getUserScoresError } from '../../store/selectors/user';
import { getSuggestions, isCallSuggestionsNew } from '../../store/selectors/callSuggestions';
import type { RootState } from '../../store/types';

type HeaderContainerProps = Partial<ComponentProps<typeof Header>>;

function HeaderContainer(props: HeaderContainerProps) {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const localization = useLocalization();

    const stateProps = useAppSelector((store: RootState) => ({
        hasError: hasTrainerError(store),
        showBoxShadow: !isTranscriptVisible(store),
        elapsedSeconds: getElapsedSeconds(store),
        userScore: getUserScores(store),
        userScoreError: getUserScoresError(store),
        whitelistPhrasesSpottingScore: getWhitelistPhrasesSpottingScore(store),
        isTrainerActive: isTrainerActive(store),
        isUpdatingTrainerStatus: isTrainerStatusUpdating(store),
        displaySnoozingControls: canDisplaySnoozingControls(store) && !IS_UI_ONLY_MODE,
        displayOnlineButton: canDisplayOnlineButton(store),
        showDashboardLink: canShowDashboardLink(store) && !isEmpty(getAuthRole(store)),
        enableCallSimulator: isCallSimulatorEnabled(store),
        isVodafone: isVodafoneUser(store),
        optInOn: isOptInOn(store),
        isOptOut: isOptOut(store),
        isCallStarted: isTrainerCallStarted(store),
        displayCallOptOutControls: canDisplayCallOptOutControls(store),
        recordCall: shouldRecordCall(store),
        authToken: getAuthToken(store) || '',
        authWithPasswordRequired: isAuthWithPasswordEnabled(store),
        showSuggestionsButton: getSuggestions(store).length > 0 && getSuggestionCallFrequency(store) > 0,
        isCallSuggestionsNew: isCallSuggestionsNew(store),
        enableTrainer: isTrainerEnabled(store),
        enableSales: isSalesEnabled(store)
    }));

    return (
        <Header
            {...props}
            {...stateProps}
            location={location}
            localization={localization}
            resumeTrainer={() => dispatch(resumeTrainer())}
            snoozeTrainer={(payload: number) => dispatch(snoozeTrainer(payload))}
            setInfinitySnooze={() => dispatch(setInfinitySnooze())}
            manualOptIn={() => dispatch(manualOptIn())}
            manualOptOut={() => dispatch(manualOptOut())}
            doLogout={() => dispatch(doLogout())}
            closeCallSuggestions={() => dispatch(closeCallSuggestions())}
            toggleCallSuggestions={() => dispatch(toggleCallSuggestions())}
        />
    );
}

export default HeaderContainer;
